import { ListItem } from "../models/ListItem";
import {
  ProposalApprovalDto,
  ProposalApprovalResponse,
} from "../models/proposal-approval.model";
import { ProposalDetailedDto } from "../models/proposal-detailed.model";
import {
  ProposalCreateFullPayload,
  ProposalCreatePayload,
  ProposalDto,
  ProposalWithPropertiesDto,
} from "../models/proposal.model";
import { httpService } from "./httpService";

import GenericService from "./genericService";

class ProposalService extends GenericService<ProposalDto, ListItem> {
  constructor() {
    super("/api/Proposals");
  }

  async getProposalListByRMCId(
    status: string,
    isActive?: boolean,
    normalize = true,
    toast = false
  ): Promise<ProposalDto[]> {
    // Controller expects Status and optional IsActive; RMCId is resolved server-side from the user context
    const params = new URLSearchParams();
    params.set("Status", status);
    if (typeof isActive !== "undefined") {
      params.set("IsActive", String(isActive));
    }

    return await httpService.get<ProposalDto[]>(
      `/api/Proposal/ProposalList?${params.toString()}`,
      undefined,
      {
        unwrap: true,
        normalize,
        toast,
      }
    );
  }

  // Use the controller route: [HttpGet("ListWithProperties")] which returns ApiResponse<IEnumerable<ProposalWithPropertiesDto>>
  async getProposalsWithPropertiesAsync(
    status: string,
    isActive?: boolean
  ): Promise<ProposalWithPropertiesDto[]> {
    const params = new URLSearchParams();
    params.set("status", status);
    if (typeof isActive !== "undefined") {
      params.set("isActive", String(isActive));
    }

    return await httpService.get<ProposalWithPropertiesDto[] | any>(
      `/api/Proposal/ListWithProperties?${params.toString()}`,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast: false,
      }
    );
  }

  async createProposal(payload: ProposalCreatePayload): Promise<ProposalDto> {
    return await httpService.post<ProposalDto>(
      "/api/Proposal/Create",
      payload,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast: true,
      }
    );
  }

  async createProposalFull(
    payload: ProposalCreateFullPayload
  ): Promise<ProposalDto> {
    return await httpService.post<ProposalDto>(
      "/api/Proposal/CreateFull",
      payload,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast: true,
      }
    );
  }

  async updateProposalFull(
    proposalId: number,
    payload: ProposalCreateFullPayload
  ): Promise<ProposalDto> {
    return await httpService.put<ProposalDto>(
      `/api/Proposal/UpdateFull/${proposalId}`,
      payload,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast: true,
      }
    );
  }

  async getDetailedProposal(proposalId: number): Promise<ProposalDetailedDto> {
    return await httpService.get<ProposalDetailedDto>(
      `/api/Proposal/GetDetailedProposal/${proposalId}`,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast: false,
      }
    );
  }

  async approveProposal(
    approvalData: ProposalApprovalDto
  ): Promise<ProposalApprovalResponse | string> {
    // Backend returns ApiResponse<string> with data = "OK" on success; handle both shapes
    return await httpService.post<ProposalApprovalResponse | string>(
      "/api/Proposal/Approve",
      approvalData,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast: true,
      }
    );
  }

  async getProposalListItems(
    rmcId: number,
    clientId: number
  ): Promise<ListItem[]> {
    return await httpService.get<ListItem[]>(
      `/api/Proposal/GetProposalListItems/${rmcId}/${clientId}`,
      undefined,
      {
        unwrap: true,
        normalize: true,
        toast: false,
      }
    );
  }

  async toggleProposalActive(
    proposalId: number,
    isActive: boolean
  ): Promise<any> {
    return await httpService.post<any>(
      `/api/Proposal/ToggleActive/${proposalId}/${isActive}`,
      undefined,
      undefined,
      {
        unwrap: false, // Keep the full ApiResponse wrapper to access correlationId
        normalize: false,
        toast: false, // Disable toast so we can handle responses manually in the component
      }
    );
  }
}

export const proposalService = new ProposalService();
